import { useMutation, useQuery, useQueryClient } from "react-query";
import useAPI from "../api/useAPI";
import useSnackbar from "./useSnackbar";
import {
  PlaylistInput,
  PlaylistMediasResponse,
  PlaylistResponse,
} from "../@types/playlist.types";

export default function usePlaylist(token: string, initialData?: any) {
  const { snackbarError, snackbarSuccess } = useSnackbar();
  const queryClient = useQueryClient();
  const { api, didLoad } = useAPI();
  const query = useQuery({
    queryKey: ["playlist", token],
    queryFn: () =>
      token && api.Playlist.get(token).then((data) => data.playlist),
    initialData,
    staleTime: Infinity,
    keepPreviousData: true,
    enabled: !!token && didLoad,
  });

  const getPlaylistMedias = useQuery({
    queryKey: ["playlist-medias", token],
    queryFn: () =>
      token &&
      api.Playlist.medias(token).then(
        (data: PlaylistMediasResponse) => data.medias,
      ),
    initialData,
    staleTime: Infinity,
    keepPreviousData: true,
    enabled: !!token && didLoad,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<PlaylistInput>) =>
      api.Playlist.update(token, data),
    onSuccess: ({ playlist }: { playlist: PlaylistResponse }) => {
      snackbarSuccess(`Playlist related information was updated`);
      queryClient.setQueryData(["playlist", token], playlist);
      queryClient.invalidateQueries({ queryKey: ["playlist", token] });
    },
    onError: (e) => {
      console.error(e);
      snackbarError(
        "Error while updating the video. Check the console for more information.",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (tkn: string) => api.Playlist.delete(tkn),
    onSuccess: () => {
      snackbarSuccess(`Video was successfully deleted`);
    },
    onError: (e) => {
      console.error(e);
      snackbarError(
        "Error while deleting the video. Check the console for more information.",
      );
    },
  });

  return {
    ...query,
    getPlaylistMedias,
    isMediasLoading: getPlaylistMedias.isFetching,
    playlist: query.data,
    medias: getPlaylistMedias.data,
    api: {
      update: updateMutation.mutate,
      delete: deleteMutation.mutate,
    },
    mutations: {
      updateMutation,
      deleteMutation,
    },
  };
}
